import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ShoppingBag, Search, Star, Check } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { SHOP_PRODUCTS, SHOP_CATEGORIES, TRUST_BADGES, SHOP_SHOWCASE } from '../mock/shop';
import { tProduct, tCat, tComingSoon } from '../i18n/shopI18n';
import OrderDirectBanner from '../components/OrderDirectBanner';

const Price = ({ p, old }) => (
  <div className="flex items-baseline gap-2">
    <span className="font-serif text-xl text-espresso">£{p.toFixed(2)}</span>
    {old ? <span className="text-sm text-mocha line-through">£{old.toFixed(2)}</span> : null}
  </div>
);

const Shop = () => {
  const { lang } = useLang();
  const { add } = useCart();
  useReveal(lang);
  const [cat, setCat] = useState('all');
  const [query, setQuery] = useState('');
  const [added, setAdded] = useState(null);

  const products = useMemo(() => {
    const q = query.toLowerCase();
    return SHOP_PRODUCTS.filter((p) => (cat === 'all' || p.category === cat) &&
      (p.name.toLowerCase().includes(q) || tProduct(p, lang).name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q)));
  }, [cat, query, lang]);

  const addToCart = (p) => { add(p, 1); setAdded(p.id); setTimeout(() => setAdded(null), 1200); };

  return (
    <div>
      {/* HERO BANNER */}
      <section className="pt-32 pb-14 satin-gold">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold-deep mb-4"><span className="w-8 h-px bg-gold-deep" />ARGE Cosmetics Store</span>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">The Professional Beauty Shop</h1>
          <p className="mt-5 text-lg text-mocha max-w-2xl mx-auto">Salon-quality hair, skin and personal care — authentic products, trusted quality, delivered across the UK.</p>
        </div>
      </section>

      {/* ORDER DIRECT & SAVE */}
      <OrderDirectBanner />

      {/* TRUST BADGES */}
      <section className="bg-espresso">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST_BADGES.map((b) => {
            const Icon = Icons[b.icon] || Icons.ShieldCheck;
            return (
              <div key={b.title} className="flex items-center gap-4 justify-center lg:justify-start">
                <Icon className="w-8 h-8 text-gold-light shrink-0" />
                <div><p className="text-ivory text-sm tracking-[0.1em] uppercase">{b.title}</p><p className="text-ivory/60 text-xs mt-0.5">{b.sub}</p></div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CATEGORY SHOWCASE */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-3xl md:text-4xl text-espresso text-center mb-10 reveal">Shop by Category</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {SHOP_SHOWCASE.map((s) => (
              <button key={s.label} onClick={() => { setCat(s.id); document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="reveal group relative overflow-hidden bg-cream aspect-square flex flex-col items-center justify-center p-6 hover-lift">
                <img src={s.image} alt={s.label} className="h-32 object-contain transition-transform duration-500 group-hover:scale-105" />
                <span className="mt-4 font-serif text-xl text-espresso group-hover:text-gold transition-colors">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CATALOGUE */}
      <section id="catalogue" className="py-10 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-8">
            <h2 className="font-serif text-3xl md:text-4xl text-espresso">Our Products</h2>
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-mocha absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products or brands…"
                className="w-full bg-ivory border border-gold-soft pl-10 pr-4 py-2.5 text-sm text-espresso placeholder-mocha/60 focus:outline-none focus:border-gold" />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-10">
            {SHOP_CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`shop-cat-${c.id}`}
                className={`px-5 py-2 text-xs tracking-[0.12em] uppercase transition-colors ${cat === c.id ? 'bg-espresso text-ivory' : 'bg-ivory text-espresso hover:bg-gold hover:text-ivory'}`}>
                {tCat(c.id, lang, c.label)}
              </button>
            ))}
          </div>

          {/* Grid */}
          {products.length === 0 ? (
            <p className="text-center text-mocha py-20">No products match your search.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {products.map((p) => {
                const t = tProduct(p, lang);
                return (
                <div key={p.id} className="reveal group bg-ivory border border-transparent hover:border-gold-soft flex flex-col hover-lift" data-testid={`product-card-${p.id}`}>
                  <Link to={`/shop/${p.id}`} className="relative block bg-white p-6 aspect-square overflow-hidden">
                    {p.badge && <span className={`absolute top-3 left-3 z-10 text-[0.6rem] tracking-[0.12em] uppercase px-2 py-0.5 ${p.comingSoon ? 'bg-espresso text-gold-light' : 'bg-gold text-espresso'}`}>{p.comingSoon ? tComingSoon(lang) : p.badge}</span>}
                    <img src={p.image} alt={t.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-[0.62rem] tracking-[0.18em] uppercase text-gold">{p.brand}</p>
                    <Link to={`/shop/${p.id}`} className="text-left text-sm text-espresso mt-1 leading-snug hover:text-gold transition-colors line-clamp-2 min-h-[2.5rem]">{t.name}</Link>
                    <div className="mt-3 flex items-center justify-between">
                      {p.comingSoon
                        ? <span className="font-serif text-lg text-gold italic">{tComingSoon(lang)}</span>
                        : <Price p={p.price} old={p.oldPrice} />}
                    </div>
                    {p.comingSoon ? (
                      <Link to="/dopamine" data-testid={`coming-soon-${p.id}`}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gold-soft text-espresso text-[0.68rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-ivory transition-colors">
                        {tComingSoon(lang)}
                      </Link>
                    ) : (
                      <button onClick={() => addToCart(p)} data-testid={`add-to-bag-${p.id}`}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-espresso text-ivory text-[0.68rem] tracking-[0.16em] uppercase hover:bg-gold transition-colors">
                        {added === p.id ? <><Check className="w-4 h-4" /> Added</> : <><ShoppingBag className="w-4 h-4" /> Add to Bag</>}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
